import type { Clock, IdGenerator } from "../ports/clock";
import type { DiaryRepository, RecipeRepository, WeekPlanRepository } from "../ports/repositories";
import type { CalendarDate } from "../../domain/shared/calendar-date";
import type { LoggingIntent } from "../../domain/check-in/state-machine";
import { assertLoggingAllowed } from "../../domain/check-in/state-machine";
import { createDiaryEntry, type DiaryEntry } from "../../domain/diary/diary-entry";
import { recipeAsFood } from "../../domain/recipes/recipe";

export interface LogRecipeInput {
  readonly recipeId: string;
  readonly servings: number;
  readonly date: CalendarDate;
  readonly meal?: string;
  readonly intent?: LoggingIntent;
}

export async function logRecipe(
  dependencies: {
    readonly recipes: RecipeRepository;
    readonly diary: DiaryRepository;
    readonly weekPlans: WeekPlanRepository;
    readonly clock: Clock;
    readonly ids: IdGenerator;
  },
  input: LogRecipeInput,
): Promise<DiaryEntry> {
  const [recipe, plan] = await Promise.all([
    dependencies.recipes.getById(input.recipeId),
    dependencies.weekPlans.getOpen(),
  ]);
  if (!recipe) throw new Error("Recipe not found");
  if (!plan) throw new Error("No active week plan");
  assertLoggingAllowed(plan, dependencies.clock.today(), input.date, input.intent);
  const entry = createDiaryEntry({
    id: dependencies.ids.next(),
    date: input.date,
    meal: input.meal,
    food: recipeAsFood(recipe),
    portion: { amount: input.servings, unit: "serving" },
    now: dependencies.clock.now(),
  });
  await dependencies.diary.save(entry);
  return entry;
}
